import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Plus, X, Pencil, Trash2, Search, ExternalLink, Pin, PinOff,
  FileText, Video, Image as ImageIcon, Link as LinkIcon, BookOpen,
  Eye, Filter, Code,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLanguage } from "../../context/LanguageContext";
import { tCrm, tFmt, crmApi, formatDate, logDevError } from "../../lib/crm";
import { toast } from "sonner";

const CONTENT_TYPE_META = {
  document: { icon: FileText, key: "document", color: "text-sky-300", bg: "bg-sky-400/10", border: "border-sky-400/30" },
  video: { icon: Video, key: "video", color: "text-fuchsia-300", bg: "bg-fuchsia-400/10", border: "border-fuchsia-400/30" },
  image: { icon: ImageIcon, key: "image", color: "text-amber-300", bg: "bg-amber-400/10", border: "border-amber-400/30" },
  link: { icon: LinkIcon, key: "link", color: "text-lime-300", bg: "bg-lime-400/10", border: "border-lime-400/30" },
  html: { icon: Code, key: "html", color: "text-orange-300", bg: "bg-orange-400/10", border: "border-orange-400/30" },
  other: { icon: BookOpen, key: "other", color: "text-zinc-300", bg: "bg-zinc-700/30", border: "border-zinc-600" },
};

const CONTENT_TYPES = Object.keys(CONTENT_TYPE_META);

// GTX services — used as top-level tabs in the Resources page.
// Each tab filters the resources collection by the `service` field.
export const GTX_SERVICES = [
  { key: "all", labelEs: "Todos", labelEn: "All", accent: "text-white" },
  { key: "import", labelEs: "GTX Import", labelEn: "GTX Import", accent: "text-sky-300" },
  { key: "buy_for_you", labelEs: "GTX Buy For You", labelEn: "GTX Buy For You", accent: "text-fuchsia-300" },
  { key: "europe_entry", labelEs: "GTX Europe Entry", labelEn: "GTX Europe Entry", accent: "text-lime-300" },
  { key: "general", labelEs: "General", labelEn: "General", accent: "text-zinc-300" },
];

export default function CRMResources({ me }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const isAdmin = me?.role === "admin";
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeService, setActiveService] = useState("all");
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      const [r, c] = await Promise.all([
        crmApi.get("/resources", { params }),
        crmApi.get("/resources/categories"),
      ]);
      setResources(r.data);
      setCategories(c.data.categories || []);
    } catch (err) {
      logDevError("CRMResources.fetch", err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Compute filtered list inline (cheap, no need for useMemo here)
  const filtered = resources.filter((r) => {
    if (activeService !== "all" && (r.service || "general") !== activeService) return false;
    if (activeCategory !== "all" && r.category !== activeCategory) return false;
    return true;
  });

  const grouped = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const k = r.category || "Other";
      (g[k] = g[k] || []).push(r);
    }
    return g;
  }, [filtered]);

  const handleOpen = async (resource) => {
    // Track view (fire and forget) then open URL in new tab.
    crmApi.post(`/resources/${resource.id}/view`).catch((err) => logDevError("view track", err));
    if (resource.content_type === "html" && resource.html_content) {
      // Render the stored HTML in a new tab via a Blob URL (no server-side route needed).
      const blob = new Blob([resource.html_content], { type: "text/html;charset=utf-8" });
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, "_blank", "noopener,noreferrer");
      // Best-effort cleanup so the object URL is released after the tab loads
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      if (!win) toast.error(t("resources.popupBlocked") || "Pop-up blocked");
      return;
    }
    if (!resource.url) {
      toast.error(t("common.error"));
      return;
    }
    window.open(resource.url, "_blank", "noopener,noreferrer");
  };

  const handleDelete = async (resource) => {
    if (!window.confirm(tFmt(language, "resources.deleteConfirm", { title: resource.title }))) return;
    try {
      await crmApi.delete(`/resources/${resource.id}`);
      toast.success(t("resources.deleted"));
      fetchAll();
    } catch (err) {
      logDevError("delete", err);
      toast.error(t("common.error"));
    }
  };

  const togglePin = async (resource) => {
    try {
      await crmApi.put(`/resources/${resource.id}`, { pinned: !resource.pinned });
      toast.success(t("resources.updated"));
      fetchAll();
    } catch (err) {
      logDevError("pin", err);
    }
  };

  const counts = useMemo(() => {
    // Category counts respect the active service tab
    const inService = activeService === "all"
      ? resources
      : resources.filter((r) => (r.service || "general") === activeService);
    const c = { all: inService.length };
    for (const r of inService) c[r.category] = (c[r.category] || 0) + 1;
    return c;
  }, [resources, activeService]);

  const serviceCounts = useMemo(() => {
    const c = { all: resources.length };
    for (const r of resources) {
      const k = r.service || "general";
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [resources]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="resources-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-2">
            {t("resources.slug")}
          </p>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tighter text-white">
            {t("resources.title")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {tFmt(language, "resources.subtitle", {
              n: resources.length,
              label: resources.length === 1 ? t("resources.labelSingular") : t("resources.labelPlural"),
            })}
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setShowNew(true)}
            data-testid="new-resource-btn"
            className="bg-lime-400 hover:bg-lime-300 text-black font-semibold rounded-md shadow-[0_0_20px_rgba(163,230,53,0.15)] hover:shadow-[0_0_28px_rgba(163,230,53,0.35)] transition-all"
          >
            <Plus className="w-4 h-4 mr-1.5" /> {t("resources.new")}
          </Button>
        )}
      </header>

      {/* Service tabs (GTX core services) */}
      <div
        className="flex flex-wrap gap-1 border-b border-zinc-800"
        data-testid="service-tabs"
      >
        {GTX_SERVICES.map((svc) => {
          const active = activeService === svc.key;
          const label = language === "es" ? svc.labelEs : svc.labelEn;
          const count = serviceCounts[svc.key] || 0;
          return (
            <button
              key={svc.key}
              type="button"
              onClick={() => setActiveService(svc.key)}
              data-testid={`service-tab-${svc.key}`}
              className={`relative px-4 py-2.5 text-sm font-mono uppercase tracking-wider transition-colors -mb-px border-b-2 ${
                active
                  ? `${svc.accent} border-current`
                  : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}
            >
              {label}
              <span className="ml-2 text-[10px] text-zinc-500">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search + category pills */}
      <div className="space-y-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <Input
            placeholder={t("resources.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="resources-search"
            className="pl-9 bg-zinc-950 border-zinc-800 text-white focus:ring-lime-400 focus:border-lime-400"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="category-pills">
          <CategoryPill
            label={t("common.all")}
            count={counts.all || 0}
            active={activeCategory === "all"}
            onClick={() => setActiveCategory("all")}
            testid="cat-all"
          />
          {categories.map((c) => (
            <CategoryPill
              key={c}
              label={c}
              count={counts[c] || 0}
              active={activeCategory === c}
              onClick={() => setActiveCategory(c)}
              testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
            />
          ))}
        </div>
      </div>

      {/* Resources grid (grouped) */}
      {loading ? (
        <div className="text-zinc-500 text-sm font-mono">cargando…</div>
      ) : filtered.length === 0 ? (
        <EmptyState isAdmin={isAdmin} onCreate={() => setShowNew(true)} />
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, items]) => (
            <section key={cat} className="space-y-3" data-testid={`section-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-baseline gap-3">
                <h2 className="text-sm font-mono uppercase tracking-[0.22em] text-zinc-400">{cat}</h2>
                <span className="text-xs text-zinc-600 font-mono">{items.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((r) => (
                  <ResourceCard
                    key={r.id}
                    resource={r}
                    isAdmin={isAdmin}
                    onOpen={() => handleOpen(r)}
                    onEdit={() => setEditing(r)}
                    onDelete={() => handleDelete(r)}
                    onPin={() => togglePin(r)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {showNew && (
        <ResourceDialog
          categories={categories}
          defaultService={activeService !== "all" ? activeService : "general"}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); fetchAll(); }}
        />
      )}
      {editing && (
        <ResourceDialog
          existing={editing}
          categories={categories}
          defaultService={editing.service || "general"}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

function CategoryPill({ label, count, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-all ${
        active
          ? "bg-lime-400 text-black"
          : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:text-white hover:border-zinc-700"
      }`}
    >
      {label}
      <span className={`font-mono text-[10px] ${active ? "text-black/60" : "text-zinc-600"}`}>{count}</span>
    </button>
  );
}

function ResourceCard({ resource, isAdmin, onOpen, onEdit, onDelete, onPin }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const meta = CONTENT_TYPE_META[resource.content_type] || CONTENT_TYPE_META.other;
  const Icon = meta.icon;
  return (
    <div
      data-testid={`resource-card-${resource.id}`}
      className="group relative flex flex-col gap-3 p-4 bg-zinc-900 border border-zinc-800 rounded-md hover:border-lime-400/40 hover:-translate-y-0.5 transition-all"
    >
      {/* Top row: icon + pin + admin actions */}
      <div className="flex items-start justify-between gap-2">
        <div className={`w-10 h-10 rounded-md ${meta.bg} ${meta.border} border flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${meta.color}`} />
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
          {isAdmin && (
            <>
              <button
                onClick={onPin}
                data-testid={`pin-${resource.id}`}
                className="w-7 h-7 inline-flex items-center justify-center text-zinc-500 rounded hover:bg-zinc-800 hover:text-amber-300"
                title={resource.pinned ? "Desfijar" : "Fijar"}
              >
                {resource.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={onEdit}
                data-testid={`edit-${resource.id}`}
                className="w-7 h-7 inline-flex items-center justify-center text-zinc-500 rounded hover:bg-zinc-800 hover:text-white"
                title="Editar"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onDelete}
                data-testid={`delete-${resource.id}`}
                className="w-7 h-7 inline-flex items-center justify-center text-zinc-500 rounded hover:bg-zinc-800 hover:text-red-400"
                title="Eliminar"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
        {resource.pinned && (
          <Pin className="absolute -top-1.5 -right-1.5 w-4 h-4 text-amber-300 fill-amber-300/30 rotate-12" />
        )}
      </div>

      {/* Title + description */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-white leading-tight mb-1 line-clamp-2">{resource.title}</h3>
        {resource.description && (
          <p className="text-xs text-zinc-500 line-clamp-2 leading-relaxed">{resource.description}</p>
        )}
      </div>

      {/* Tags */}
      {resource.tags && resource.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {resource.tags.slice(0, 4).map((tag) => (
            <span key={tag} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer: meta + open */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-800">
        <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-600">
          <Eye className="w-3 h-3" />
          {resource.view_count || 0}
          <span className="text-zinc-700">·</span>
          {formatDate(resource.created_at)}
        </div>
        <button
          onClick={onOpen}
          data-testid={`open-${resource.id}`}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-lime-300 rounded hover:bg-lime-400/10 transition"
        >
          {t("common.open")} <ExternalLink className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ isAdmin, onCreate }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  return (
    <div className="text-center py-16 bg-zinc-900 border border-dashed border-zinc-800 rounded-md">
      <BookOpen className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
      <h3 className="text-lg font-medium text-white mb-1">{t("resources.empty")}</h3>
      <p className="text-sm text-zinc-500 mb-4">
        {isAdmin ? t("resources.emptyAdminHint") : t("resources.emptyAgentHint")}
      </p>
      {isAdmin && (
        <Button onClick={onCreate} className="bg-lime-400 hover:bg-lime-300 text-black font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> {t("resources.emptyCta")}
        </Button>
      )}
    </div>
  );
}

// ============================================================
// Resource dialog (create/edit)
// ============================================================
function ResourceDialog({ existing, categories, defaultService = "general", onClose, onSaved }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [form, setForm] = useState({
    title: existing?.title || "",
    description: existing?.description || "",
    category: existing?.category || (categories[0] || "Other"),
    service: existing?.service || defaultService || "general",
    url: existing?.url || "",
    thumbnail_url: existing?.thumbnail_url || "",
    content_type: existing?.content_type || "link",
    html_content: existing?.html_content || "",
    pinned: existing?.pinned || false,
    tags: (existing?.tags || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const isHtml = form.content_type === "html";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
    };
    if (isHtml) {
      // For HTML resources the URL is internal — set a placeholder so the
      // existing card UI / Open button still works visually.
      payload.url = payload.url || "html://embedded";
    } else {
      // Non-html resources don't need html_content
      payload.html_content = "";
    }
    try {
      if (existing) {
        await crmApi.put(`/resources/${existing.id}`, payload);
        toast.success(t("resources.updated"));
      } else {
        await crmApi.post("/resources", payload);
        toast.success(t("resources.created"));
      }
      onSaved();
    } catch (err) {
      logDevError("save resource", err);
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-lime-400 mb-1">
              {existing ? `resources/${existing.id.slice(0, 8)}` : "resources/new"}
            </div>
            <h2 className="text-xl font-medium text-white">{existing ? t("resources.formEditTitle") : t("resources.formNewTitle")}</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1 rounded hover:bg-zinc-800">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="resource-form">
          <Field label={t("resources.fieldTitle")} required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="resource-title" />
          <div>
            <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("resources.fieldDescription")}</Label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              data-testid="resource-description"
              className="w-full mt-1 bg-zinc-950 border border-zinc-800 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-lime-400 focus:border-lime-400"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("resources.fieldService")}</Label>
            <select
              value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })}
              data-testid="resource-service"
              className="w-full mt-1 bg-zinc-950 border border-zinc-800 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-lime-400"
            >
              {GTX_SERVICES.filter((s) => s.key !== "all").map((s) => (
                <option key={s.key} value={s.key}>
                  {language === "es" ? s.labelEs : s.labelEn}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelectField label={t("resources.fieldCategory")} value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={categories.length ? categories : ["Other"]} testid="resource-category" />
            <SelectField
              label={t("resources.fieldType")}
              value={form.content_type}
              onChange={(v) => setForm({ ...form, content_type: v })}
              options={CONTENT_TYPES}
              labels={Object.fromEntries(CONTENT_TYPES.map((k) => [k, t(`resources.type.${k}`)]))}
              testid="resource-type"
            />
          </div>
          {isHtml ? (
            <div>
              <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider flex items-center gap-2">
                <Code className="w-3 h-3 text-orange-300" />
                {t("resources.fieldHtml")} *
              </Label>
              <textarea
                value={form.html_content}
                onChange={(e) => setForm({ ...form, html_content: e.target.value })}
                rows={10}
                required
                spellCheck={false}
                data-testid="resource-html-content"
                placeholder="<!doctype html>&#10;<html>&#10;  <body>&#10;    <h1>Hola</h1>&#10;  </body>&#10;</html>"
                className="w-full mt-1 bg-zinc-950 border border-zinc-800 text-white text-xs font-mono rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400"
              />
              <p className="text-[10px] text-zinc-500 mt-1">{t("resources.htmlHint")}</p>
            </div>
          ) : (
            <Field label={t("resources.fieldUrl")} required value={form.url} onChange={(v) => setForm({ ...form, url: v })} testid="resource-url" placeholder={t("resources.fieldUrlPlaceholder")} />
          )}
          <Field label={t("resources.fieldThumbnail")} value={form.thumbnail_url} onChange={(v) => setForm({ ...form, thumbnail_url: v })} testid="resource-thumbnail" />
          <Field label={t("resources.fieldTags")} value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testid="resource-tags" placeholder={t("resources.fieldTagsPlaceholder")} />
          <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300 pt-1">
            <input
              type="checkbox"
              checked={form.pinned}
              onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
              data-testid="resource-pinned"
              className="accent-lime-400"
            />
            <Pin className="w-3.5 h-3.5 text-amber-300" />
            {t("resources.fieldPinned")}
          </label>
          <div className="flex gap-2 pt-3">
            <Button type="button" variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white">
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="resource-submit-btn"
              className="flex-1 bg-lime-400 hover:bg-lime-300 text-black font-semibold"
            >
              {saving ? t("common.saving") : existing ? t("resources.btnSave") : t("resources.btnCreate")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 bg-zinc-950 border-zinc-800 text-white focus:ring-lime-400 focus:border-lime-400"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, labels, testid }) {
  return (
    <div>
      <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{label}</Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full mt-1 bg-zinc-950 border border-zinc-800 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-lime-400 focus:border-lime-400"
      >
        {options.map((o) => <option key={o} value={o}>{labels?.[o] || o}</option>)}
      </select>
    </div>
  );
}
